import { isMonetaryValue, MonetaryValue, MoneyPerAreaValue } from 'ComponentLibrary/src/common/money'
import { AreaValue, isAreaValue } from 'ComponentLibrary/src/common/area'
import { isPercentage, Percentage } from 'ComponentLibrary/src/common/percentage'
import { isYearPeriod, YearPeriod } from 'ComponentLibrary/src/common/year-period'
import { decimalToLocalizedString } from 'ComponentLibrary/src/common/numbers'

export type ValueType = MonetaryValue | MoneyPerAreaValue | AreaValue | Percentage | YearPeriod | number | string

export const formatExcelCell = (
    valueToFormat?: ValueType | null,
    isPercentagePoints?: boolean,
    percentagePointUnit = 'BP',
    locale = 'de-DE'
) => {
    const valuationInfo = getFormattedValueAndUnit(valueToFormat, isPercentagePoints, percentagePointUnit, locale)

    if (!valuationInfo) {
        return { value: '', numFmt: undefined }
    }

    const { value, unit } = valuationInfo

    if (unit === percentagePointUnit) {
        return {
            value,
            numFmt: percentagePointUnit === 'BP' ? `#,##0 "${percentagePointUnit}"` : `#,##0.00 "${percentagePointUnit}"`,
        }
    }

    switch (unit) {
        case '%':
            return {
                value: value / 100,
                numFmt: '0.00%',
            }
        case '€':
            return {
                value,
                numFmt: '€#,##0.00',
            }
        case 'm²':
        case 'm² Wfl.':
        case 'm² living area':
            return {
                value,
                numFmt: `#,##0 "${unit}"`,
            }
        case 'Jahr':
        case 'Jahre':
        case 'year':
        case 'years':
            return {
                value,
                numFmt: `# "${unit}"`,
            }
        case undefined:
        default:
            return {
                value,
                numFmt: undefined,
            }
    }
}

const getFormattedValueAndUnit = (value?: ValueType | null, isPercentagePoints?: boolean, percentagePointUnit = 'BP', locale = 'de-DE') => {
    if (isMonetaryValue(value)) {
        return {
            formattedValue: decimalToLocalizedString(value.toDecimal(), false, 2, undefined, locale),
            value: value.toDecimal(),
            unit: value.currencySymbol(),
        }
    }
    if (isAreaValue(value)) {
        return {
            formattedValue: value.format({ withUnit: false, locale }),
            value: value.value,
            unit: value.unitSymbol(locale),
        }
    }
    if (isPercentage(value)) {
        const percentageValue = value.toPercentage()
        return {
            formattedValue: value.format({ withUnit: false, locale }),
            value: isPercentagePoints ? (percentagePointUnit === 'BP' ? percentageValue * 100 : percentageValue) : percentageValue,
            unit: isPercentagePoints ? percentagePointUnit : '%',
        }
    }
    if (isYearPeriod(value)) {
        const unit = locale === 'en-US' ? (Math.abs(value.value) === 1 ? 'year' : 'years') : Math.abs(value.value) === 1 ? 'Jahr' : 'Jahre'
        return {
            formattedValue: decimalToLocalizedString(value.value, false, 2, undefined, locale),
            value: value.value,
            unit,
        }
    }

    if (typeof value === 'number') {
        return {
            formattedValue: decimalToLocalizedString(value, false, 2, undefined, locale),
            value,
            unit: undefined,
        }
    }

    if (typeof value === 'string') {
        return {
            formattedValue: value,
            value,
            unit: undefined,
        }
    }
    return undefined
}
